/**
 * Spielt gerade ein eingebetteter Player? (13.10.2026, Tickets 3232, 3230, 3231)
 *
 * Die Musik-Karte bettet YouTube ein. Mit `enablejsapi=1` und dem Gruß `listening` schickt der Player
 * Zustandswechsel als postMessage (`onStateChange`, `infoDelivery`); 1 = spielt, alles andere nicht.
 * EIN Besitzer und EIN Hörer: nur diese Datei hält den Stand. Ein Player, der aus dem Dokument verschwindet, zählt nicht mehr.
 *
 * Drei Nutzer: die Zugzeile (`wiedergabeJetzt`), der Hinweis „Startet nichts?“ (Rückruf `beiStart`, Ticket 3231)
 * und das Mikro nach der Musik (Ereignis `voice-v8:tts-audio-end` mit `musikEnde`, Ticket 3230 — app.ts entscheidet
 * mit denselben Stufen wie nach einer Antwort).
 */
import type { Wiedergabe } from "../../shared/wiedergabe.js";

const YT = "https://www.youtube.com";
/** Der Gruß wird wiederholt, bis der Player antwortet (er hört erst, wenn er geladen ist). */
const GRUSS_TAKT_MS = 500;
const GRUSS_MAX = 20;
/** So lange muss es still bleiben, bevor „Musik zu Ende“ gilt (ein Vorspulen pausiert kurz). */
const ENDE_WARTE_MS = 1500;

interface Beobachtet {
  beiStart?: () => void;
  gehoert: boolean;
}
const beobachtet = new Map<HTMLIFrameElement, Beobachtet>();
const spielt = new Set<HTMLIFrameElement>();
let hoert = false;
let endeTimer: ReturnType<typeof setTimeout> | null = null;
/** 17.10.2026: die Seite hat die Musik selbst angehalten (lange-spur.ts) — das ist kein „Musik zu Ende“ und öffnet kein Mikro. */
let eigenePause = false;

/** Der Zustand aus einer Nachricht des Players: true = spielt, false = nicht, null = keine Aussage. */
export function spielzustandAus(roh: unknown): boolean | null {
  let d: unknown = roh;
  if (typeof roh === "string") {
    try {
      d = JSON.parse(roh);
    } catch {
      return null;
    }
  }
  const o = d as { event?: unknown; info?: unknown } | null;
  if (!o || typeof o !== "object") return null;
  if (o.event === "onStateChange" && typeof o.info === "number") return o.info === 1;
  if (o.event === "infoDelivery" && o.info && typeof o.info === "object") {
    const s = (o.info as { playerState?: unknown }).playerState;
    if (typeof s === "number") return s === 1;
  }
  return null;
}

/** Läuft sonst noch etwas? Räumt Rahmen ab, die aus dem Dokument verschwunden sind. */
function laeuftNoch(): boolean {
  for (const f of [...spielt]) if (!f.isConnected) spielt.delete(f);
  return spielt.size > 0;
}

function musikEndeMelden(): void {
  if (endeTimer) clearTimeout(endeTimer);
  endeTimer = setTimeout(() => {
    endeTimer = null;
    if (laeuftNoch()) return;
    window.dispatchEvent(new CustomEvent("voice-v8:tts-audio-end", { detail: { endetMitFrage: false, musikEnde: true } }));
  }, ENDE_WARTE_MS);
}

function beiNachricht(ev: MessageEvent): void {
  if (ev.origin !== YT || !ev.source) return;
  const z = spielzustandAus(ev.data);
  if (z === null) return;
  for (const [rahmen, b] of beobachtet) {
    if (rahmen.contentWindow !== ev.source) continue;
    b.gehoert = true;
    if (z) {
      if (endeTimer) {
        clearTimeout(endeTimer);
        endeTimer = null;
      }
      spielt.add(rahmen);
      eigenePause = false;
      const start = b.beiStart;
      b.beiStart = undefined;
      start?.();
    } else if (spielt.delete(rahmen)) {
      if (eigenePause) {
        if (!laeuftNoch()) eigenePause = false;
      } else musikEndeMelden();
    }
    return;
  }
}

/** Den Player der Karte beobachten: ein Hörer für alle, der Gruß bei jedem Laden — wiederholt, bis der Player antwortet. */
export function beobachteSpieler(rahmen: HTMLIFrameElement, beiStart?: () => void): void {
  if (!hoert) {
    hoert = true;
    window.addEventListener("message", beiNachricht);
  }
  const b: Beobachtet = { beiStart, gehoert: false };
  beobachtet.set(rahmen, b);
  rahmen.addEventListener("load", () => {
    let n = 0;
    const gruss = (): void => {
      if (!rahmen.isConnected) {
        stopp(); // Karte entfernt: Timer weg, Rahmen vergessen
        return;
      }
      try {
        rahmen.contentWindow?.postMessage(JSON.stringify({ event: "listening", id: 1, channel: "widget" }), YT);
      } catch {
        /* ohne Gruß keine Meldung — der Zug bleibt dann "stille", wie bisher */
      }
      if (++n >= GRUSS_MAX || b.gehoert) stopp();
    };
    const timer = setInterval(gruss, GRUSS_TAKT_MS);
    const stopp = (): void => {
      clearInterval(timer);
      if (!rahmen.isConnected) {
        beobachtet.delete(rahmen);
        spielt.delete(rahmen);
      }
    };
    gruss();
  });
}

/** Was läuft jetzt? Nur Musik-Karten werden beobachtet, darum nie "video". */
export function wiedergabeJetzt(): Wiedergabe {
  return laeuftNoch() ? "musik" : "stille";
}

/**
 * Martin greift von Hand ein (Sprechtaste, getippte Frage, Abspielknopf): die Musik wird angehalten (`pauseVideo` per
 * postMessage an den eingebetteten Player, `enablejsapi=1` ist gesetzt), damit die Antwort nicht gegen sie anspricht.
 * Das Anhalten meldet KEIN „Musik zu Ende“ (kein Mikro nach der Musik). true = mindestens ein Player wurde angesprochen.
 */
export function pausiereMusik(): boolean {
  let gesendet = false;
  for (const f of [...spielt]) {
    if (!f.isConnected) continue;
    try {
      f.contentWindow?.postMessage(JSON.stringify({ event: "command", func: "pauseVideo", args: "" }), YT);
      gesendet = true;
    } catch {
      /* ohne Befehl läuft die Musik weiter — die Antwort wartet dann */
    }
  }
  if (gesendet) eigenePause = true;
  return gesendet;
}
